import React, { useState, useEffect, useRef } from "react";
import { Handle, Position, NodeProps, useReactFlow, useNodes } from "reactflow";
import {
  Trash2,
  Plus,
  BetweenHorizontalStart,
  FlaskConical,
} from "lucide-react";
import { Input } from "@components/ui/input";
import { Button } from "@components/ui/button";
import { Switch } from "@components/ui/switch";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import Confirmation from "@components/common/ConfirmDialog";
import { toast } from "@components/ui/use-toast";
import { getConnectedInputs, getExecutionPath } from "../common/nodeDataFlow";
import { handlerStyle, unwrapJsonData } from "../common/commonData";
import ResponseViewer from "../common/ResponseViewer";
import NodeContentSection from "../common/NodeContentSection";
import { executeWorkflow } from "../createPluginApis";
import {
  getNodeExecutionParameters,
  buildWorkflowPayload,
  handleNodeExecutionResults,
  findDownstreamNodes,
} from "../utils/nodeExecutionUtils";
import { getNodeBackendType } from "../utils/nodeTransformUtils";
import CollapsedNodeView from "../common/NodeHeaderView";
import NodeContextMenu, {
  useNodeContextMenu,
  createContextMenuItems,
} from "../common/NodeContextMenu";
import PaginatedInputViewer from "../common/PaginatedInputViewer";
import { FullscreenWrapper } from "../common/withFullscreen";
import { FullscreenToggleButton } from "../common/FullscreenToggleButton";
import { ValidatedTitleInput } from "../components/ValidatedTitleInput";
import { useResizablePanels } from "../hooks/useResizablePanels";
import { PanelDivider } from "../common/PanelDivider";

interface SplitOutNodeData {
  label?: string;
  uid: string;
  title: string;
  description?: string;
  fieldsToSplitOut?: string[];
  include?: "noOtherFields" | "allOtherFields" | "selectedOtherFields";
  fieldsToInclude?: string[];
  options?: {
    disableDotNotation?: boolean;
    destinationFieldName?: string;
  };
  onDelete?: (id: string) => void;
  onDataChange?: (id: string, updates: any) => void;
  borderColor?: string;
  shadowColor?: string;
}

const SplitOutNode: React.FC<NodeProps<SplitOutNodeData>> = ({ id, data }) => {
  const label = data.label || "Split Out";
  const [uid, setUid] = useState(data.uid || "");
  const [title, setTitle] = useState(data.title || "");
  const [description, setDescription] = useState(data.description || "");
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isTitleValid, setIsTitleValid] = useState(true);

  // Context menu
  const {
    showContextMenu,
    contextMenuPosition,
    handleContextMenu,
    closeContextMenu,
  } = useNodeContextMenu(id);

  // Split Out specific state
  const [fieldsToSplitOut, setFieldsToSplitOut] = useState<string[]>(
    data.fieldsToSplitOut && data.fieldsToSplitOut.length > 0
      ? data.fieldsToSplitOut
      : [""], // Always start with at least one empty field
  );
  const [include, setInclude] = useState<
    "noOtherFields" | "allOtherFields" | "selectedOtherFields"
  >(data.include || "noOtherFields");

  const [fieldsToInclude, setFieldsToInclude] = useState<string[]>(
    data.fieldsToInclude && data.fieldsToInclude.length > 0
      ? data.fieldsToInclude
      : [""],
  );

  const [options, setOptions] = useState<{
    disableDotNotation?: boolean;
    destinationFieldName?: string;
  }>(data.options || {});

  const [showOptionsDropdown, setShowOptionsDropdown] = useState(false);

  // Input/Output state
  const [inputData, setInputData] = useState<any[]>([]);
  const [outputData, setOutputData] = useState<any>(null);
  const [errorResponse, setErrorResponse] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const reactFlowInstance = useReactFlow();
  const { containerRef, widths, startDrag } = useResizablePanels([
    33.33, 33.33, 33.34,
  ]);
  const { getNodes, getEdges } = useReactFlow();
  const nodes = useNodes();

  // Auto-generate UID if not present
  useEffect(() => {
    if (!uid) {
      const newUid = `split_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      setUid(newUid);
    }
  }, []);

  // Load saved parameters from data prop (when loading from backend)
  useEffect(() => {
    const params = (data as any).storedParameters || {};

    if (params.fieldToSplitOut) {
      setFieldsToSplitOut([params.fieldToSplitOut]);
    }
    if (params.include) {
      setInclude(params.include);
    }
    if (params.fieldsToInclude) {
      const fieldsArray = Array.isArray(params.fieldsToInclude)
        ? params.fieldsToInclude
        : typeof params.fieldsToInclude === "string"
          ? [params.fieldsToInclude]
          : [""];
      setFieldsToInclude(fieldsArray);
    }
    if (params.options) {
      setOptions(params.options);
    }
  }, []); // Only run once on mount

  // Refresh input data when node connections change
  useEffect(() => {
    refreshInputData();
    // Only refresh every 3 seconds to avoid performance issues
    const interval = setInterval(refreshInputData, 3000);
    return () => clearInterval(interval);
  }, [id]); // Only re-run if node ID changes

  // Sync state to parent with storedParameters
  useEffect(() => {
    if (!data.onDataChange) return;

    // Build storedParameters for backend
    const splitOutFields =
      fieldsToSplitOut[0]
        ?.split(",")
        .map((f: string) => f.trim())
        .filter((f: string) => f.length > 0) || [];

    const nodeOptions = {
      disableDotNotation: options?.disableDotNotation ?? false,
      destinationFieldName: options?.destinationFieldName?.trim() ?? "",
    };

    // Map frontend enum values to backend expected values
    const mapIncludeToBackend = (frontendValue: string): string => {
      // Backend expects the same values as frontend
      switch (frontendValue) {
        case "noOtherFields":
          return "noOtherFields";
        case "allOtherFields":
          return "allOtherFields";
        case "selectedOtherFields":
          return "selectedOtherFields";
        default:
          return "noOtherFields"; // Default to noOtherFields
      }
    };

    const storedParameters: any = {
      fieldToSplitOut: splitOutFields.join(","),
      include: mapIncludeToBackend(include),
      options: nodeOptions,
    };

    // Only add fieldsToInclude for selectedOtherFields mode
    if (include === "selectedOtherFields") {
      const includeFields =
        fieldsToInclude[0]
          ?.split(",")
          .map((f: string) => f.trim())
          .filter((f: string) => f.length > 0) || [];
      storedParameters.fieldsToInclude =
        includeFields.length > 0 ? includeFields.join(",") : "";
    }
    // Don't include fieldsToInclude for other modes (noOtherFields, allOtherFields)

    // Build update object - only include fieldsToInclude when in selectedOtherFields mode
    const updates: any = {
      uid,
      title,
      description,
      fieldsToSplitOut,
      include,
      options,
      outputData,
      response: outputData,
      sub_type: "split_out",
      type: "data_transformation",
      storedParameters,
    };

    // Only include fieldsToInclude property for selectedOtherFields mode
    if (include === "selectedOtherFields") {
      updates.fieldsToInclude = fieldsToInclude;
    }

    data.onDataChange(id, updates);
  }, [
    uid,
    title,
    description,
    fieldsToSplitOut,
    include,
    fieldsToInclude,
    options,
    outputData,
  ]);

  // Ref to track previous outputData for sync effect
  const prevOutputDataRef = useRef<any>(null);

  // Sync outputData from node data (updated during workflow execution)
  // Use deep comparison to prevent unnecessary updates
  useEffect(() => {
    const newOutputData = (data as any).outputData || (data as any).response;

    // Skip if no new data
    if (!newOutputData) {
      // Clear output if explicitly set to null/undefined
      if (outputData !== null && newOutputData === null) {
        prevOutputDataRef.current = null;
        setOutputData(null);
      }
      return;
    }

    // Skip if it's the same reference (optimization)
    if (prevOutputDataRef.current === newOutputData) {
      return;
    }

    // Deep comparison - only update if stringified content changed
    const newDataStr = JSON.stringify(newOutputData);
    const prevDataStr = prevOutputDataRef.current
      ? JSON.stringify(prevOutputDataRef.current)
      : null;

    if (newDataStr !== prevDataStr) {
      prevOutputDataRef.current = newOutputData;
      setOutputData(newOutputData);
    } else {
      // Content is the same, just update the reference to prevent future comparisons
      prevOutputDataRef.current = newOutputData;
    }
  }, [(data as any).outputData, (data as any).response]);

  // Sync error from node data (set during global workflow execution)
  useEffect(() => {
    const executionStatus = (data as any).executionStatus;
    const error = (data as any).error;

    // If node failed during global execution, show error in ResponseViewer
    if (executionStatus === "failed" && error) {
      setErrorResponse(error);
    } else if (executionStatus === "success") {
      // Clear error when node succeeds
      setErrorResponse(null);
    }
  }, [(data as any).executionStatus, (data as any).error]);

  const refreshInputData = () => {
    const nodes = getNodes();
    const edges = getEdges();
    const connectedInputs = getConnectedInputs(id, edges, nodes);

    // Only update if the data actually changed (prevent unnecessary re-renders)
    setInputData((prevData) => {
      if (JSON.stringify(prevData) === JSON.stringify(connectedInputs)) {
        return prevData; // No change, return previous to avoid re-render
      }
      return connectedInputs;
    });
  };

  const handleDelete = (nodeId: string) => {
    setDeleteId(nodeId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (deleteId && data.onDelete) {
      data.onDelete(deleteId);
    }
    setShowDeleteConfirm(false);
    setDeleteId(null);
  };

  const handleDrop = (e: React.DragEvent, fieldIndex: number) => {
    e.preventDefault();
    e.stopPropagation();

    const dragData = e.dataTransfer.getData("application/json");
    if (dragData) {
      try {
        const parsedData = JSON.parse(dragData);
        const fieldPath = parsedData.path;

        if (fieldPath) {
          // Extract full field path from the dragged data
          // Strip {{ }} wrappers first
          let cleanPath = fieldPath
            .replace(/^\{\{\s*/, "")
            .replace(/\s*\}\}$/, "");
          // Remove $json. or config. prefix
          cleanPath = cleanPath
            .replace(/^\$json\./, "")
            .replace(/^config\./, "");
          // Keep the full path with array indices as-is
          const fullFieldPath = cleanPath;

          // Get current value and split by comma
          const currentValue = fieldsToSplitOut[fieldIndex] || "";
          const existingFields = currentValue
            .split(",")
            .map((f) => f.trim())
            .filter((f) => f.length > 0);

          // Add new field if not already present
          if (!existingFields.includes(fullFieldPath)) {
            existingFields.push(fullFieldPath);
          }

          // Update with comma-separated list
          const newFields = [...fieldsToSplitOut];
          newFields[fieldIndex] = existingFields.join(", ");
          setFieldsToSplitOut(newFields);
        }
      } catch (error) {
        console.error("Error parsing drag data:", error);
      }
    }
  };

  const updateFieldValue = (index: number, value: string) => {
    const newFields = [...fieldsToSplitOut];
    newFields[index] = value;
    setFieldsToSplitOut(newFields);
  };

  const addOption = (
    optionType: "disableDotNotation" | "destinationFieldName",
  ) => {
    if (optionType === "disableDotNotation") {
      setOptions({ ...options, disableDotNotation: false });
    } else if (optionType === "destinationFieldName") {
      setOptions({
        ...options,
        destinationFieldName: options.destinationFieldName || "",
      });
    }
    setShowOptionsDropdown(false);
  };

  const removeOption = (
    optionType: "disableDotNotation" | "destinationFieldName",
  ) => {
    const newOptions = { ...options };
    delete newOptions[optionType];
    setOptions(newOptions);
  };

  const handleDestinationFieldDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const dragData = e.dataTransfer.getData("application/json");
    if (dragData) {
      try {
        const parsedData = JSON.parse(dragData);
        const fieldPath = parsedData.path;

        if (fieldPath) {
          // Extract full field path from the dragged data
          // Strip {{ }} wrappers first
          let cleanPath = fieldPath
            .replace(/^\{\{\s*/, "")
            .replace(/\s*\}\}$/, "");
          // Remove $json. or config. prefix
          cleanPath = cleanPath
            .replace(/^\$json\./, "")
            .replace(/^config\./, "");
          // Keep the full path with array indices as-is
          const fullFieldPath = cleanPath;

          // Get current value and split by comma
          const currentValue = options.destinationFieldName || "";
          const existingFields = currentValue
            .split(",")
            .map((f) => f.trim())
            .filter((f) => f.length > 0);

          // Add new field if not already present
          if (!existingFields.includes(fullFieldPath)) {
            existingFields.push(fullFieldPath);
          }

          // Update with comma-separated list
          setOptions({
            ...options,
            destinationFieldName: existingFields.join(", "),
          });
        }
      } catch (error) {
        console.error("Error parsing drag data:", error);
      }
    }
  };

  const handleFieldsToIncludeDrop = (
    e: React.DragEvent,
    fieldIndex: number,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    const dragData = e.dataTransfer.getData("application/json");
    if (dragData) {
      try {
        const parsedData = JSON.parse(dragData);
        const fieldPath = parsedData.path;

        if (fieldPath) {
          // Extract full field path from the dragged data
          // Strip {{ }} wrappers first
          let cleanPath = fieldPath
            .replace(/^\{\{\s*/, "")
            .replace(/\s*\}\}$/, "");
          // Remove $json. or config. prefix
          cleanPath = cleanPath
            .replace(/^\$json\./, "")
            .replace(/^config\./, "");
          // Keep the full path with array indices as-is
          const fullFieldPath = cleanPath;

          // Get current value and split by comma
          const currentValue = fieldsToInclude[fieldIndex] || "";
          const existingFields = currentValue
            .split(",")
            .map((f) => f.trim())
            .filter((f) => f.length > 0);

          // Add new field if not already present
          if (!existingFields.includes(fullFieldPath)) {
            existingFields.push(fullFieldPath);
          }

          // Update with comma-separated list
          const newFields = [...fieldsToInclude];
          newFields[fieldIndex] = existingFields.join(", ");
          setFieldsToInclude(newFields);
        }
      } catch (error) {
        console.error("Error parsing drag data:", error);
      }
    }
  };

  const updateFieldsToIncludeValue = (index: number, value: string) => {
    const newFields = [...fieldsToInclude];
    newFields[index] = value;
    setFieldsToInclude(newFields);
  };

  // Execute workflow
  const executeSplitOut = async () => {
    if (!inputData?.length) {
      toast({
        variant: "destructive",
        title: "No Input Data",
        description: "Connect a node to provide input data",
      });
      return;
    }

    if (!fieldsToSplitOut[0] || !fieldsToSplitOut[0].trim()) {
      toast({
        variant: "destructive",
        title: "No Fields Configured",
        description: "Add at least one field to split out",
      });
      return;
    }

    if (!title.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Title is required for the split out node",
        duration: 3000,
      });
      return;
    }

    // Get active mesh flow from sessionStorage
    const activeMeshFlowStr = sessionStorage.getItem("activeMeshFlow");
    if (!activeMeshFlowStr) {
      toast({
        variant: "destructive",
        title: "Error",
        description:
          "No active mesh flow. Please create or select a mesh flow first.",
        duration: 4000,
      });
      return;
    }

    setOutputData(null);
    setErrorResponse(null);
    setLoading(true);

    try {
      const activeMeshFlow = JSON.parse(activeMeshFlowStr);

      // Get all nodes and edges from React Flow
      // Get plugin ID from session
      const pluginId = sessionStorage.getItem("editPluginId") || "";

      const allNodes = reactFlowInstance.getNodes();
      const allEdges = reactFlowInstance.getEdges();

      // Get execution path: only nodes from start to current node (no downstream nodes)
      const executableNodes = getExecutionPath(id, allNodes, allEdges);

      // Mark all executable nodes as pending
      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          // Check if this node is in the execution path
          const isExecutable = executableNodes.some(
            (n: any) => n.id === node.id,
          );
          if (isExecutable) {
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "pending",
                executionResult: undefined,
                error: undefined,
              },
            };
          }
          return node;
        }),
      );

      // Build nodes array for workflow
      const workflowNodes = executableNodes.map((node) => {
        let nodeType = "core";
        let subType = "http_request";
        let parameters;

        if (node.id === id) {
          // This is the current SplitOutNode - build from local state
          const splitOutFields = fieldsToSplitOut[0]
            .split(",")
            .map((f) => f.trim())
            .filter((f) => f.length > 0);

          const nodeOptions = {
            disableDotNotation: options.disableDotNotation ?? false,
            destinationFieldName: options.destinationFieldName?.trim() ?? "",
          };

          const nodeParameters = {
            fieldToSplitOut: splitOutFields.join(","),
            include,
            options: nodeOptions,
          } as any;

          if (include === "selectedOtherFields") {
            if (fieldsToInclude[0]) {
              const includeFields = fieldsToInclude[0]
                .split(",")
                .map((f) => f.trim())
                .filter((f) => f.length > 0);
              (nodeParameters as any).fieldsToInclude =
                includeFields.length > 0 ? includeFields.join(",") : "";
            } else {
              (nodeParameters as any).fieldsToInclude = "";
            }
          }

          nodeType = "data_transformation";
          subType = "split_out";
          parameters = {
            ...nodeParameters,
            label: label,
          };
        } else {
          // Other nodes - use centralized utility (checks storedParameters first)
          // Determine backend type and sub_type using centralized mapping
          const backendTypeInfo = getNodeBackendType(node.type || "custom");
          nodeType = backendTypeInfo.type;
          subType = backendTypeInfo.sub_type;

          // Get parameters using centralized utility
          parameters = getNodeExecutionParameters(node, pluginId).parameters;
        }

        return {
          uid: node.data.uid || node.id,
          title: node.data.title || node.data.label || "Untitled",
          type: nodeType,
          sub_type: subType,
          version: "1.0",
          position: [node.position.x, node.position.y],
          parameters,
        };
      });

      // Create a mapping from React Flow node IDs to UIDs
      const nodeIdToUidMap: Record<string, string> = {};
      executableNodes.forEach((node) => {
        const nodeUid = node.data.uid || node.id;
        nodeIdToUidMap[node.id] = nodeUid;
      });

      // Build connections object from edges using UIDs (only between executable nodes)
      const connections: any = {};
      allEdges.forEach((edge) => {
        const sourceUid = nodeIdToUidMap[edge.source];
        const targetUid = nodeIdToUidMap[edge.target];

        // Only include connections between executable nodes
        if (!sourceUid || !targetUid) return;

        // Only include connections between executable nodes
        if (!sourceUid || !targetUid) return;

        if (!connections[sourceUid]) {
          connections[sourceUid] = {
            main: [],
          };
        }

        if (!connections[sourceUid].main[0]) {
          connections[sourceUid].main[0] = [];
        }

        connections[sourceUid].main[0].push({
          node: targetUid,
          type: "main",
          index: 0,
        });
      });

      // Build complete workflow payload
      const workflowPayload = buildWorkflowPayload(
        activeMeshFlow,
        workflowNodes,
        connections,
      );

      const result = await executeWorkflow(workflowPayload);

      // Update ALL executed nodes with their results (not just the clicked node)
      if (result?.results?.[0]?.node_results) {
        const nodeResults = result.results[0].node_results;

        // Use the utility function to handle node execution results
        const { hasErrors, errorMessage, errorNodeTitle } =
          handleNodeExecutionResults(reactFlowInstance, nodeResults, allEdges);

        // Extract this node's output for local state
        const resultsMap = new Map<string, any>();
        nodeResults.forEach((nodeResult: any) => {
          resultsMap.set(nodeResult.uid, nodeResult);
        });

        const currentNodeResult = resultsMap.get(uid || id);
        if (currentNodeResult) {
          // Check if this node failed
          const isFailed =
            currentNodeResult.status === "error" ||
            currentNodeResult.status === "failed" ||
            currentNodeResult.status === "failure";

          if (isFailed) {
            // Show error in ResponseViewer but don't pass to downstream nodes
            setErrorResponse(currentNodeResult.error || "Execution failed");
            setOutputData(null);
          } else if (currentNodeResult.output_data) {
            // Success - extract and display output data
            const responseData = unwrapJsonData(currentNodeResult.output_data);
            setOutputData(responseData);
            setErrorResponse(null);
          }
        }

        // Show appropriate toast based on execution result
        if (hasErrors) {
          const errorDescription = errorMessage
            ? `${errorNodeTitle}: ${errorMessage}`
            : "One or more nodes failed during execution";

          const shortError =
            errorDescription.length > 150
              ? errorDescription.substring(0, 150) + "..."
              : errorDescription;

          toast({
            variant: "destructive",
            title: "Execution Failed",
            description: shortError,
            duration: 7000,
          });
        } else {
          toast({
            variant: "success",
            title: "Success",
            description: "Split out executed successfully",
            duration: 3000,
          });
        }
      } else {
        // Fallback - no node results in response
        const isSuccess =
          result?.success === true ||
          result?.status === "success" ||
          result?.body?.status === 1 ||
          result?.body?.status === "1" ||
          result?.status === 1;

        if (isSuccess) {
          reactFlowInstance.setNodes((nodes) =>
            nodes.map((node) => {
              if (node.id === id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "success",
                  },
                };
              }
              return node;
            }),
          );

          toast({
            variant: "success",
            title: "Success",
            description: result?.message || "Split out executed successfully",
            duration: 3000,
          });
        } else {
          const allEdges = reactFlowInstance.getEdges();
          const downstreamNodes = findDownstreamNodes(id, allEdges);

          reactFlowInstance.setNodes((nodes) =>
            nodes.map((node) => {
              if (node.id === id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "failed",
                    error:
                      result?.body?.message ||
                      result?.message ||
                      "Execution failed",
                  },
                };
              } else if (downstreamNodes.has(node.id)) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "failed",
                    error: "Not executed due to upstream failure",
                  },
                };
              }
              return node;
            }),
          );

          toast({
            variant: "destructive",
            title: "Execution Failed",
            description:
              result?.body?.message ||
              result?.message ||
              "Split out execution failed",
            duration: 4000,
          });
        }
      }
    } catch (error: any) {
      console.error("SplitOut Execution Error:", error);
      setErrorResponse(error?.message || "Split out execution failed");
      setOutputData(null);

      const allEdges = reactFlowInstance.getEdges();
      const downstreamNodes = findDownstreamNodes(id, allEdges);

      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "failed",
                error: error?.message || "Split out execution failed",
              },
            };
          } else if (downstreamNodes.has(node.id)) {
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "failed",
                error: "Not executed due to upstream failure",
              },
            };
          }
          return node;
        }),
      );

      toast({
        variant: "destructive",
        title: "Error",
        description: error?.message || "Split out execution failed",
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Copy output to clipboard
  const handleCopyOutput = () => {
    const dataToCopy = errorResponse || outputData;
    if (!dataToCopy) return;
    navigator.clipboard.writeText(JSON.stringify(dataToCopy, null, 2));
    toast({
      variant: "success",
      title: "Copied!",
      description: "Output copied to clipboard.",
      duration: 2000,
    });
  };

  // Get display data for InputViewer - prefer response data, fallback to configData
  const getDisplayData = (input: any) => {
    // If response exists and has data, use it
    if (input.response && Object.keys(input.response).length > 0) {
      return input.response;
    }

    // Check outputData as well
    if (input.outputData && Object.keys(input.outputData).length > 0) {
      return input.outputData;
    }

    // Otherwise, use filtered configData
    const displayData: any = {};
    const EXCLUDE = [
      "uid",
      "title",
      "description",
      "label",
      "position",
      "nodeType",
      "isDecript",
      "isDecrypted",
      "testConnectionBtn",
      "tabId",
      "configType",
      "onDelete",
      "onDataChange",
      "header",
      "borderColor",
      "shadowColor",
    ];

    if (input.configData) {
      Object.entries(input.configData)
        .filter(([key]) => !EXCLUDE.includes(key))
        .forEach(([key, value]) => (displayData[key] = value));
    }

    return displayData;
  };

  // Render input data section with pagination
  const renderInputData = () => {
    if (!inputData?.length) {
      return (
        <div className="text-xs text-muted-foreground p-4 border rounded-md bg-muted/20 text-center">
          <div className="font-medium">No Input Connected</div>
          <div>Connect a node to see data here</div>
        </div>
      );
    }

    // Build sources array for PaginatedInputViewer (handles tabs internally)
    const sources = inputData.map((input, index) => {
      // Generate label: if from switch node, use "Rule X", otherwise use original label
      let displayLabel = input.label || `Source ${index + 1}`;

      // Check if label indicates switch node (e.g., "switch", "SWITCH")
      if (displayLabel.toLowerCase().includes("switch")) {
        displayLabel = `Rule ${index + 1}`;
      }

      return {
        label: displayLabel,
        data: getDisplayData(input),
        isConfig: !!input.configData, // Mark as config if source is configuration
      };
    });

    return (
      <PaginatedInputViewer
        sources={sources}
        onRefresh={refreshInputData}
        sourceCount={inputData.length}
      />
    );
  };

  return (
    <FullscreenWrapper
      id={id}
      isExpanded={isExpanded}
      executionStatus={(data as any).executionStatus}
      borderColor={data.borderColor}
      shadowColor={data.shadowColor}
      onContextMenu={handleContextMenu}
      expandedClassName="w-[90vw] !max-w-[1000px] flex flex-col"
    >
      <Confirmation
        text="Are you sure you want to delete this node?"
        confirmAction={confirmDelete}
        rejectAction={() => setShowDeleteConfirm(false)}
        confirmText="Confirm"
        rejectText="Cancel"
        show={showDeleteConfirm}
        closeDialog={() => setShowDeleteConfirm(false)}
      />

      {/* Context Menu */}
      <NodeContextMenu
        show={showContextMenu}
        position={contextMenuPosition}
        onClose={closeContextMenu}
        items={[
          createContextMenuItems.expandCollapse(isExpanded, () =>
            setIsExpanded((prev) => !prev),
          ),
          createContextMenuItems.delete(
            () => handleDelete(id),
            !!data.onDelete,
          ),
        ]}
      />

      {/* Handles */}
      <Handle type="target" position={Position.Left} className={handlerStyle} />
      <Handle
        type="source"
        position={Position.Right}
        className={handlerStyle}
      />

      {/* Header */}
      <div
        className={`flex items-center justify-between gap-4 rounded-md transition-all duration-500 flex-shrink-0 ${
          isExpanded ? "p-3" : "!p-3"
        }`}
      >
        <CollapsedNodeView
          icon={
            <BetweenHorizontalStart
              className={
                (data as any).executionStatus === "running" ||
                (data as any).executionStatus === "pending"
                  ? "animate-bounce-horizontal"
                  : ""
              }
            />
          }
          label={label}
          title={title}
          iconSize={35}
          borderColor={data.borderColor}
          onToggle={() => setIsExpanded((prev) => !prev)}
          isExpanded={isExpanded}
          executionStatus={(data as any).executionStatus}
          description={description}
        />
        <FullscreenToggleButton isExpanded={isExpanded} />
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {/* Metadata Form */}
          <div className="mt-2 p-4 border-t flex items-start gap-4 flex-shrink-0">
            <div className="space-y-1 w-1/2">
              <label className="text-xs font-medium text-muted-foreground">
                Title <span className="text-primary">*</span>
              </label>
              <ValidatedTitleInput
                id={id}
                title={title}
                onChange={setTitle}
                nodes={nodes}
                dataTitle={data.title}
                onValidationChange={setIsTitleValid}
              />
            </div>
            <div className="space-y-1 w-1/2">
              <label className="text-xs font-medium text-muted-foreground">
                Description (optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter node description"
                className="w-full p-2 border rounded text-xs resize-none text-muted-foreground bg-background"
                rows={2}
              />
            </div>
          </div>

          {/* 3 Section Grid */}
          <div
            ref={containerRef}
            className="flex gap-0 p-4 border-t flex-1 min-h-0 overflow-hidden"
          >
            {/* Left Section - Input Data (4/12 columns) */}
            <div
              style={{ width: `${widths[0]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection hasRelative={true} scrollable={false}>
                {renderInputData()}
              </NodeContentSection>
            </div>

            <PanelDivider onMouseDown={startDrag(0)} />

            {/* Middle Section - Parameters (4/12 columns) */}
            <div
              style={{ width: `${widths[1]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection hasRelative={true} scrollable={false}>
                <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col gap-2">
                  <h3 className="text-sm font-semibold sticky top-0 bg-background z-10 pb-2 border-b">
                    Parameters
                  </h3>

                  {/* Fields To Split Out */}
                  <div className="space-y-2 border rounded-md p-3 bg-muted/20">
                    <label className="text-xs font-semibold">
                      Fields To Split Out
                    </label>
                    <div
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.classList.add(
                          "ring-2",
                          "ring-primary",
                          "bg-primary/5",
                        );
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.classList.remove(
                          "ring-2",
                          "ring-primary",
                          "bg-primary/5",
                        );
                      }}
                      onDrop={(e) => {
                        e.currentTarget.classList.remove(
                          "ring-2",
                          "ring-primary",
                          "bg-primary/5",
                        );
                        handleDrop(e, 0);
                      }}
                      className="w-full border border-transparent rounded transition-all"
                    >
                      <Input
                        value={fieldsToSplitOut[0] || ""}
                        onChange={(e) => updateFieldValue(0, e.target.value)}
                        placeholder="Drag fields here (e.g., userId,name)"
                        className="text-xs h-7 font-mono w-full"
                      />
                    </div>
                  </div>

                  {/* Include Dropdown */}
                  <div className="space-y-2 border rounded-md p-3 bg-muted/20">
                    <label className="text-xs font-semibold">Include</label>
                    <Select
                      value={include}
                      onValueChange={(value: any) => setInclude(value)}
                    >
                      <SelectTrigger className="w-full text-xs h-8">
                        <SelectValue placeholder="Select include option" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value="noOtherFields" className="text-xs">
                            No other Fields
                          </SelectItem>
                          <SelectItem
                            value="allOtherFields"
                            className="text-xs"
                          >
                            All other fields
                          </SelectItem>
                          <SelectItem
                            value="selectedOtherFields"
                            className="text-xs"
                          >
                            Selected Other Fields
                          </SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Fields To Include - Conditional */}
                  {include === "selectedOtherFields" && (
                    <div className="space-y-2 border rounded-md p-3 bg-muted/20">
                      <label className="text-xs font-semibold">
                        Fields To Include
                      </label>
                      <div
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.currentTarget.classList.add(
                            "ring-2",
                            "ring-primary",
                            "bg-primary/5",
                          );
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.currentTarget.classList.remove(
                            "ring-2",
                            "ring-primary",
                            "bg-primary/5",
                          );
                        }}
                        onDrop={(e) => {
                          e.currentTarget.classList.remove(
                            "ring-2",
                            "ring-primary",
                            "bg-primary/5",
                          );
                          handleFieldsToIncludeDrop(e, 0);
                        }}
                        className="w-full border border-transparent rounded transition-all"
                      >
                        <Input
                          value={fieldsToInclude[0] || ""}
                          onChange={(e) =>
                            updateFieldsToIncludeValue(0, e.target.value)
                          }
                          placeholder="Drag fields here (e.g., userId, name)"
                          className="text-xs h-7 font-mono w-full"
                        />
                      </div>
                    </div>
                  )}

                  {/* Options Section */}
                  <div className="space-y-2 border rounded-md p-3 bg-muted/20">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-semibold">Options</label>
                      <div className="relative">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() =>
                            setShowOptionsDropdown(!showOptionsDropdown)
                          }
                        >
                          <Plus size={14} />
                        </Button>

                        {showOptionsDropdown && (
                          <div className="absolute right-0 top-8 z-10 bg-background border shadow-lg rounded-md py-1 min-w-[180px]">
                            <div
                              className={`px-3 py-2 text-xs cursor-pointer ${
                                options.disableDotNotation !== undefined
                                  ? "opacity-50 cursor-not-allowed"
                                  : "hover:bg-accent"
                              }`}
                              onClick={() => {
                                if (options.disableDotNotation === undefined) {
                                  addOption("disableDotNotation");
                                }
                              }}
                            >
                              Disable Dot Notation
                              {options.disableDotNotation !== undefined && (
                                <span className="ml-2 text-muted-foreground">
                                  (Added)
                                </span>
                              )}
                            </div>
                            <div
                              className={`px-3 py-2 text-xs cursor-pointer ${
                                options.destinationFieldName !== undefined
                                  ? "opacity-50 cursor-not-allowed"
                                  : "hover:bg-accent"
                              }`}
                              onClick={() => {
                                if (
                                  options.destinationFieldName === undefined
                                ) {
                                  addOption("destinationFieldName");
                                }
                              }}
                            >
                              Destination Field Name
                              {options.destinationFieldName !== undefined && (
                                <span className="ml-2 text-muted-foreground">
                                  (Added)
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Show active options */}
                    <div className="space-y-2">
                      {options.disableDotNotation !== undefined && (
                        <div className="flex items-center justify-between p-2 bg-background border rounded text-xs">
                          <span className="flex-1">Disable Dot Notation</span>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={options.disableDotNotation}
                              onCheckedChange={(checked) =>
                                setOptions({
                                  ...options,
                                  disableDotNotation: checked,
                                })
                              }
                            />
                            <Trash2
                              size={14}
                              className="cursor-pointer text-red-500 hover:text-red-700 transition"
                              onClick={() => removeOption("disableDotNotation")}
                            />
                          </div>
                        </div>
                      )}

                      {options.destinationFieldName !== undefined && (
                        <div className="space-y-1 p-2 bg-background border rounded">
                          <div className="flex items-center justify-between">
                            <label className="text-xs text-muted-foreground">
                              Destination Field Name
                            </label>
                            <Trash2
                              size={14}
                              className="cursor-pointer text-red-500 hover:text-red-700 transition"
                              onClick={() =>
                                removeOption("destinationFieldName")
                              }
                            />
                          </div>
                          <div
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              e.currentTarget.classList.add(
                                "ring-2",
                                "ring-primary",
                                "bg-primary/5",
                              );
                            }}
                            onDragLeave={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              e.currentTarget.classList.remove(
                                "ring-2",
                                "ring-primary",
                                "bg-primary/5",
                              );
                            }}
                            onDrop={(e) => {
                              e.currentTarget.classList.remove(
                                "ring-2",
                                "ring-primary",
                                "bg-primary/5",
                              );
                              handleDestinationFieldDrop(e);
                            }}
                            className="w-full border border-transparent rounded transition-all"
                          >
                            <Input
                              value={options.destinationFieldName || ""}
                              onChange={(e) =>
                                setOptions({
                                  ...options,
                                  destinationFieldName: e.target.value,
                                })
                              }
                              placeholder="Drag fields here (e.g., userId, name)"
                              className="text-xs h-7 font-mono"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Execute Button */}
                <div className="flex-shrink-0 pt-2 border-t bg-background">
                  <Button
                    onClick={executeSplitOut}
                    disabled={
                      !inputData ||
                      inputData.length === 0 ||
                      !fieldsToSplitOut[0] ||
                      !fieldsToSplitOut[0].trim() ||
                      !isTitleValid
                    }
                    className="w-full"
                    size="sm"
                  >
                    <FlaskConical size={14} className="mr-2" />
                    Execute step
                  </Button>
                </div>
              </NodeContentSection>
            </div>

            <PanelDivider onMouseDown={startDrag(1)} />

            {/* Right Section - Response (4/12 columns) */}
            <div
              style={{ width: `${widths[2]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection scrollable={false} hasRelative={true}>
                <ResponseViewer
                  response={errorResponse || outputData}
                  loading={loading}
                  emptyMessage="Execute split out to see output preview"
                  loadingMessage="Processing..."
                  onCopy={handleCopyOutput}
                  isError={!!errorResponse}
                  isLoopChild={(data as any).is_loop_child || false}
                  totalIterations={(data as any).total_iterations || 0}
                  iterations={(data as any).iterations || []}
                  fillParent={true}
                  onResponseChange={(newData) => {
                    setOutputData(newData);
                    data.onDataChange?.(id, {
                      response: newData,
                      manuallyEdited: true,
                    });
                  }}
                />
              </NodeContentSection>
            </div>
          </div>
        </div>
      )}
    </FullscreenWrapper>
  );
};

export default SplitOutNode;
